import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Repeat } from "lucide-react";

export const RECURRING_SAVINGS_MESSAGE =
  "Save money with recurring lawn care. Weekly customers receive our lowest rates and priority scheduling.";

export const FREQUENCY_OPTIONS = [
  { value: "one_time",      label: "One-Time Service" },
  { value: "weekly",        label: "Weekly (Every 1 Week)" },
  { value: "biweekly",      label: "Bi-Weekly (Every 2 Weeks)" },
  { value: "every_3_weeks", label: "Every 3 Weeks" },
  { value: "every_4_weeks", label: "Every 4 Weeks" },
  { value: "monthly",       label: "Monthly" },
  { value: "every_6_weeks", label: "Every 6 Weeks" },
  { value: "custom",        label: "Custom Schedule" },
];

export const DOW_OPTIONS = [
  { value: "any", label: "Any day" },
  { value: "mon", label: "Monday" },
  { value: "tue", label: "Tuesday" },
  { value: "wed", label: "Wednesday" },
  { value: "thu", label: "Thursday" },
  { value: "fri", label: "Friday" },
  { value: "sat", label: "Saturday" },
  { value: "sun", label: "Sunday" },
];

export const TIME_WINDOW_OPTIONS = [
  { value: "morning",   label: "Morning (7am – 11am)" },
  { value: "midday",    label: "Midday (11am – 1pm)" },
  { value: "afternoon", label: "Afternoon (1pm – 5pm)" },
  { value: "evening",   label: "Evening (5pm – 8pm)" },
  { value: "any",       label: "Any time" },
];

/**
 * Reusable block of recurring service form fields.
 * Controlled component — owner manages `value` and forwards updates via `onChange`.
 * value shape: { service_frequency, custom_days, preferred_service_day, preferred_time_window, start_date }
 */
export default function RecurringFrequencyFields({ value, onChange, services = [], showServiceFields = true, compact = false }) {
  const v = value || {};
  const set = (patch) => onChange?.({ ...v, ...patch });
  const isCustom = v.service_frequency === "custom";

  return (
    <div className={compact ? "space-y-2.5" : "space-y-3"} data-testid="recurring-fields">
      <div
        className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/40 px-3.5 py-2.5 flex items-start gap-2.5"
        data-testid="recurring-savings-banner"
      >
        <Repeat className="w-4 h-4 text-[#047857] mt-0.5 shrink-0" />
        <p className="text-[12px] leading-snug text-emerald-900 font-medium">
          {RECURRING_SAVINGS_MESSAGE}
        </p>
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Service frequency</Label>
        <Select value={v.service_frequency || "one_time"} onValueChange={(val) => set({ service_frequency: val })}>
          <SelectTrigger className="mt-1.5" data-testid="rec-frequency">
            <SelectValue placeholder="Pick frequency" />
          </SelectTrigger>
          <SelectContent>
            {FREQUENCY_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value} data-testid={`rec-freq-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isCustom && (
        <div data-testid="rec-custom-days-block">
          <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Days between visits</Label>
          <Input
            type="number"
            min={1}
            max={365}
            value={v.custom_days ?? ""}
            onChange={(e) => set({ custom_days: parseInt(e.target.value || "0", 10) || null })}
            placeholder="e.g. 10"
            className="mt-1.5"
            data-testid="rec-custom-days"
          />
          <div className="mt-1 text-[11px] text-gray-500">Enter the number of days between each visit.</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Preferred day</Label>
          <Select value={v.preferred_service_day || "any"} onValueChange={(val) => set({ preferred_service_day: val })}>
            <SelectTrigger className="mt-1.5" data-testid="rec-pref-day"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOW_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Time window</Label>
          <Select value={v.preferred_time_window || "morning"} onValueChange={(val) => set({ preferred_time_window: val })}>
            <SelectTrigger className="mt-1.5" data-testid="rec-time-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIME_WINDOW_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Start date</Label>
        <Input
          type="date"
          value={v.start_date || ""}
          onChange={(e) => set({ start_date: e.target.value })}
          className="mt-1.5"
          data-testid="rec-start-date"
        />
      </div>

      {showServiceFields && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Service</Label>
            <Select value={v.service_key || "lawn_mowing"} onValueChange={(val) => set({ service_key: val })}>
              <SelectTrigger className="mt-1.5" data-testid="rec-service-key"><SelectValue /></SelectTrigger>
              <SelectContent>
                {services.length === 0 ? (
                  <SelectItem value="lawn_mowing">Lawn Mowing</SelectItem>
                ) : services.map(s => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">Square feet</Label>
            <Input
              type="number"
              min={0}
              value={v.square_feet ?? 2000}
              onChange={(e) => set({ square_feet: parseInt(e.target.value || "0", 10) || 0 })}
              className="mt-1.5"
              data-testid="rec-square-feet"
            />
          </div>
        </div>
      )}
    </div>
  );
}
